import { useMemo } from 'react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes } from '@har-insight/parser';

const COLORS: Record<string, string> = {
  js: '#f59e0b',
  css: '#8b5cf6',
  html: '#3b82f6',
  json: '#22c55e',
  image: '#ec4899',
  font: '#6366f1',
  xml: '#14b8a6',
  video: '#f43f5e',
  audio: '#a855f7',
  text: '#64748b',
  other: '#94a3b8',
};

export function ContentTypeChart() {
  const { stats, filteredEntries } = useHARStore();

  const data = useMemo(() => {
    if (!stats) return [];

    const sizeByType: Record<string, number> = {};
    const countByType: Record<string, number> = {};

    filteredEntries.forEach(entry => {
      const type = getSimpleType(entry.response.content.mimeType);
      sizeByType[type] = (sizeByType[type] || 0) + (entry.response.content.size || 0);
      countByType[type] = (countByType[type] || 0) + 1;
    });

    return Object.entries(sizeByType)
      .map(([name, size]) => ({
        name: name.toUpperCase(),
        count: countByType[name] || 0,
        size,
        color: COLORS[name] || COLORS.other,
      }))
      .sort((a, b) => b.size - a.size);
  }, [stats, filteredEntries]);

  if (data.length === 0) return null;

  const maxSize = Math.max(...data.map(d => d.size));

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1">
        <div className="space-y-3">
          {data.map((item) => (
            <div key={item.name} className="flex items-center gap-3">
              <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 w-12 flex-shrink-0">
                {item.name}
              </span>
              <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden border border-black/5 dark:border-white/5">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${(item.size / maxSize) * 100}%`,
                    backgroundColor: item.color,
                  }}
                />
              </div>
              <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 w-16 text-right flex-shrink-0">
                {formatBytes(item.size)}
              </span>
              <span className="text-[10px] font-bold text-slate-400 w-6 text-right flex-shrink-0">
                {item.count}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function getSimpleType(mimeType: string): string {
  if (!mimeType) return 'other';
  const type = mimeType.split(';')[0].trim().toLowerCase();

  if (type.includes('javascript') || type.includes('ecmascript')) return 'js';
  if (type.includes('css')) return 'css';
  if (type.includes('html')) return 'html';
  if (type.includes('json')) return 'json';
  if (type.includes('xml')) return 'xml';
  if (type.includes('image')) return 'image';
  if (type.includes('font')) return 'font';
  if (type.includes('video')) return 'video';
  if (type.includes('audio')) return 'audio';
  if (type.includes('text')) return 'text';

  return 'other';
}
